import React, { memo, useMemo } from 'react';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import type { Settings } from 'react-slick';
import { RichTextBlock } from 'prismic-reactjs';
import { defaultTheme } from '@styles/theme.styled';
import { ImageProps } from '@components/image';
import AnimatedElement from '@components/animated-element';
import Text from '@components/text';
import getLinkConfig from '@helpers/get-link-config';
import { Wrapper } from '@components/wrapper.styled';
import {
  FeatureWrapper,
  StyledSection,
  TextContent,
  TitleContainer,
  StyledText,
  StyledLinkComponent,
  CarouselColumn,
  StyledSlider,
  Slide,
  CarouselImage,
} from './feature-carousel.styled';

export interface FeatureItemProps {
  image?: ImageProps;
}

export interface FeatureCarouselProps {
  title?: string;
  subheading?: RichTextBlock[];
  description?: RichTextBlock[];
  backgroundColor?: keyof typeof defaultTheme.backgroundColors;
  link?: {
    uid?: string;
    url?: string;
  };
  linkLabel?: string;
  items: FeatureItemProps[];
  'aria-label'?: string;
}

const FeatureCarousel: React.FC<FeatureCarouselProps> = ({
  title,
  subheading,
  description,
  link,
  linkLabel,
  backgroundColor = 'white',
  items,
  'aria-label': ariaLabel,
}) => {
  const linkConfig = getLinkConfig(link);

  const hasSubheading = !!subheading?.length;
  const hasDescription = !!description?.length;
  const hasLink = !!linkConfig.url;
  const hasTitleOrSubheading = !!(title || hasSubheading);
  const slides = useMemo(() => {
    return items.filter((item) => item?.image?.url || item?.image?.gatsbyImageData);
  }, [items]);
  const hasSlides = slides.length > 0;

  const sliderSettings = useMemo<Settings>(() => {
    const multipleSlides = slides.length > 1;
    const settings: Settings = {
      dots: multipleSlides,
      arrows: false,
      infinite: multipleSlides,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: multipleSlides,
      autoplaySpeed: 5000,
      speed: 600,
      cssEase: 'ease-in-out',
      pauseOnHover: true,
      pauseOnFocus: true,
      adaptiveHeight: true,
    };

    return settings;
  }, [slides.length]);

  return (
    <StyledSection backgroundColor={backgroundColor}>
      <Wrapper>
        <FeatureWrapper aria-label={ariaLabel || title}>
          <TextContent>
            {hasTitleOrSubheading && (
              <TitleContainer>
                {title && (
                  <Text
                    tag="h2"
                    type="title"
                    stringText={title}
                    fontSize="heading3"
                    margin="none"
                    fontColor={backgroundColor === 'dark' ? 'white' : 'secondary'}
                  />
                )}
                {hasSubheading && (
                  <Text
                    type="subheading"
                    richText={subheading}
                    margin="none"
                    fontColor={backgroundColor === 'dark' ? 'white' : 'secondary'}
                  />
                )}
              </TitleContainer>
            )}
            {hasDescription && (
              <StyledText
                type="description"
                richText={description}
                margin="none"
                fontColor={backgroundColor === 'dark' ? 'white' : 'dark'}
              />
            )}
            {hasLink && (
              <StyledLinkComponent
                displayArrow={false}
                type="button"
                isExternal={linkConfig.isExternal}
                url={linkConfig.url}
                title={linkLabel || 'Explore all products and features'}
                aria-label={linkLabel || 'Explore all products and features'}
              />
            )}
          </TextContent>
          {hasSlides && (
            <CarouselColumn>
              <AnimatedElement
                animationType="slideInLeft"
                delay={0.125}
                threshold={0.125}
                rootMargin="0px"
                width="100%"
              >
                <StyledSlider
                  {...sliderSettings}
                  aria-label={ariaLabel || `${title || 'Feature'} carousel`}
                >
                  {slides.map((item, index) => (
                    <Slide key={item.image?.url || index}>
                      <CarouselImage
                        gatsbyImageData={item.image?.gatsbyImageData}
                        alt={item.image?.alt || title || 'Feature image'}
                        loading="lazy"
                        objectFit="contain"
                      />
                    </Slide>
                  ))}
                </StyledSlider>
              </AnimatedElement>
            </CarouselColumn>
          )}
        </FeatureWrapper>
      </Wrapper>
    </StyledSection>
  );
};

export default memo(FeatureCarousel);
